/**
 * 방문자가 무엇에 관심을 보였는지 서버로 보낸다(inc/insights.php).
 *
 * 여기서 보내는 것은 브라우저 안에서만 벌어져 서버가 알 수 없는 일들뿐이다:
 *   impression   그리드에서 카드가 실제로 눈에 들어왔다
 *   source_click 인스타 원본으로 나갔다
 *   apply_click  요약의 신청 링크로 나갔다
 * 검색어·태그·계정 필터는 결과 건수를 아는 서버가 직접 남긴다.
 *
 * 한 건씩 보내지 않고 모아 뒀다가 한 번에 보낸다. 스크롤 한 번에 노출이 여러 건
 * 생기는데 그때마다 요청을 만들면 목록을 훑는 동작 자체가 느려진다. 페이지를
 * 떠날 때는 keepalive 로 밀어 넣어 마지막 묶음이 사라지지 않게 한다.
 *
 * 실패해도 아무것도 하지 않는다 — 통계가 화면 동작을 방해하면 안 된다.
 */
const FLUSH_DELAY = 2000; // 모았다가 보내기까지 기다리는 시간(ms)
const MAX_QUEUE = 50; // 한 번에 보내는 최대 건수 — 서버도 같은 수에서 자른다
const VISIBLE_RATIO = 0.5; // 카드가 이만큼 보이면 노출로 친다
const VISIBLE_MS = 1000; // 그 상태로 이만큼 머물러야 한다

const InitAcademyTracker = () => {
  const config = window.qrshopAcademy;

  // trackEnabled 는 서버가 정한다(운영자 본인은 세지 않는다).
  // wp_localize_script 가 참/거짓을 '1'/'' 로 바꿔 내려보내므로 값으로 비교하지 않는다.
  if (!config?.restUrl || !config.trackEnabled) {
    return;
  }

  const endpoint = `${config.restUrl}/track`;
  let queue = [];
  let timer = null;

  const flush = () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }

    if (!queue.length) {
      return;
    }

    const body = JSON.stringify({ events: queue.slice(0, MAX_QUEUE) });

    queue = queue.slice(MAX_QUEUE);

    try {
      // keepalive — 탭이 닫히는 중에도 요청이 끝까지 간다.
      fetch(endpoint, {
        method: 'POST',
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body,
      }).catch(() => {});
    } catch {
      // 통계 때문에 화면이 멈추는 일은 없어야 한다.
    }

    if (queue.length) {
      flush();
    }
  };

  const push = (event, id) => {
    const postId = Number(id);

    if (!event || !postId) {
      return;
    }

    queue.push({ event, id: postId });

    // 바깥으로 나가는 클릭은 이 탭이 곧 사라질 수 있으니 기다리지 않는다.
    if (event === 'source_click' || event === 'apply_click' || queue.length >= MAX_QUEUE) {
      flush();
      return;
    }

    timer = timer || setTimeout(flush, FLUSH_DELAY);
  };

  const cardId = (element) => element?.closest('[data-academy-id]')?.dataset.academyId;

  // -------------------------------------------------------------------------
  // 노출 — 카드가 화면에 머문 뒤에야 센다
  // -------------------------------------------------------------------------

  // 지나가듯 스친 카드는 노출이 아니다. 화면 밖으로 나가면 타이머를 취소한다.
  const pending = new WeakMap();
  const counted = new WeakSet();

  const observer =
    typeof IntersectionObserver === 'undefined'
      ? null
      : new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const card = entry.target;

              if (!entry.isIntersecting) {
                clearTimeout(pending.get(card));
                pending.delete(card);
                return;
              }

              if (counted.has(card) || pending.has(card)) {
                return;
              }

              pending.set(
                card,
                setTimeout(() => {
                  counted.add(card);
                  pending.delete(card);
                  observer.unobserve(card);
                  push('impression', card.dataset.academyId);
                }, VISIBLE_MS)
              );
            });
          },
          { threshold: VISIBLE_RATIO }
        );

  const watch = (root = document) => {
    if (!observer) {
      return;
    }

    root.querySelectorAll?.('.academy-card[data-academy-id]').forEach((card) => {
      if (!counted.has(card)) {
        observer.observe(card);
      }
    });
  };

  watch();

  // 카드는 '더 보기'로 이어붙거나 필터가 바뀌면 통째로 교체된다. 어느 쪽이든
  // 새로 들어온 것만 골라 관찰을 건다.
  const results = document.getElementById('academy-results');

  if (results && typeof MutationObserver !== 'undefined') {
    new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) {
            return;
          }

          if (node.matches?.('.academy-card[data-academy-id]')) {
            observer?.observe(node);
          }

          watch(node);
        });
      });
    }).observe(results, { childList: true, subtree: true });
  }

  // -------------------------------------------------------------------------
  // 클릭 — 문서에 위임한다(카드가 언제 다시 그려지든 그대로 동작한다)
  // -------------------------------------------------------------------------

  document.addEventListener('click', (event) => {
    const tracked = event.target.closest('[data-academy-track]');

    if (!tracked) {
      return;
    }

    const id = Number(cardId(tracked)) || 0;

    push(tracked.dataset.academyTrack === 'apply' ? 'apply_click' : 'source_click', id);
  });

  // 페이지를 떠날 때 남은 묶음을 보낸다. pagehide 는 모바일 사파리에서
  // beforeunload 가 오지 않는 경우까지 덮는다.
  window.addEventListener('pagehide', flush);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flush();
    }
  });
};

export default InitAcademyTracker;
